import type { QueryClient } from '@tanstack/react-query'
import { z } from 'zod'

import { marketEndpoints } from '@/api/endpointCache'
import { marketApi, type MarketRange } from '@/features/market/api/marketApi'
import {
  barCandles,
  breadthFromWire,
  chartCandles,
  chartPoints,
  displayDate,
  displayTime,
  indexDisplayName,
  intradayPoints,
  kittaToCrore,
  marketStateIsOpen,
  moodDayFromWire,
  moodFromWire,
  rupeesToCrore,
} from '@/features/market/api/marketMappers'
import { lenientList } from '@/schemas/common'
import {
  candlePointSchema,
  indexIntradaySchema,
  indexSummarySchema,
  indicesPageSchema,
  marketMoodSchema,
  moodDaySchema,
  tickerTapeSchema,
  type IndexIntraday,
  type IndexRange,
  type IndexSummary,
  type IndicesPage,
  type MarketMood,
  type MoodDay,
  type TickerTape,
} from '@/schemas/market'

/**
 * A bar series as the app carries it: the symbol mm-be RESOLVED (not the one
 * that was asked for — `/charts/history/` matches case-insensitively and
 * prefers an active listing, so "nabil" comes back as "NABIL"), and the bars.
 *
 * `bars` is a plain array and is EMPTY, not absent, when the symbol has no
 * drawable session — unlike `IndexSummary.candles`, which is optional because
 * the panel there has a line to fall back to and a sentence to print. A screen
 * reading this one has neither: it asks "do I have a series?", and `.length`
 * is the answer.
 */
const dailyBarsSchema = z.object({
  symbol: z.string().min(1),
  bars: z.array(candlePointSchema),
})

export type DailyBars = z.infer<typeof dailyBarsSchema>

const moodHistorySchema = lenientList(moodDaySchema, 'fear and greed')

/**
 * Seven UI ranges, five on the wire.
 *
 * `live`, `1d` and `1w` all resolve to `'1W'` because mm-be publishes no
 * intraday index series: the history endpoint reads `stock_price`, which holds
 * ONE row per company per DATE, so "today, by the minute" is not in the table
 * it queries. The chart datafeed agrees from the other end — `/charts/history/`
 * serves daily bars only and 400s on any other resolution — and files intraday
 * under "not in this slice" pending the `live_stock_price` ticks table.
 * Reporting a week as a "1D" chart is the one thing this table must not be
 * read as doing.
 *
 * So the Market Overview panel no longer draws a 1D chip at all (see
 * `OverviewIndexPanel`'s RANGES), which is what `screens/stock`'s own
 * OverviewPanel has always done. Two callers still ask for a short range and
 * still get a week: `TodaysBriefCard` (`live`) and `IndicesChartCard` (`1d`).
 * Both are outside this change; both are the same lie in a smaller frame, and
 * both are fixed by deleting a chip once someone owns those files. The entries
 * stay here because those callers exist — removing them would not make the
 * data appear, it would only stop the app compiling.
 */
const RANGE_BY_UI: Record<IndexRange, MarketRange> = {
  live: '1W',
  '1d': '1W',
  '1w': '1W',
  '1m': '1M',
  '3m': '3M',
  '1y': '1Y',
  all: 'ALL',
}

/**
 * The wire range a UI range resolves to.
 *
 * `useIndexSummary` keys on the UI range, so `live`, `1d` and `1w` are three
 * cache entries holding byte-identical data and, before the endpoint cache
 * below, three identical `/market/indices/NEPSE/history/?range=1W` requests.
 * The requests are now one — the endpoint cache keys on the RESOLVED range, so
 * all three chips share a single entry and a single fetch.
 *
 * The three duplicate SCREEN entries remain, and cannot be fixed from here:
 * `marketKeys.indexSummary` builds the key. Keying it on `resolvedRange(range)`
 * instead of `range` would collapse them too, at the cost of nothing — the
 * payloads are already identical — but that is `keys.ts`'s call to make, not
 * this module's, and nothing there does it today. Not exported: no caller
 * outside this module reaches for it, so it stays a private helper until one
 * does — widening it back is a one-word change if `keys.ts` ever wants it.
 */
const resolvedRange = (range: IndexRange): MarketRange => RANGE_BY_UI[range]

/**
 * Whether mm-be serves the intraday index series: true since `range=1D` became a point a minute
 * (mm-be PR #99, 2026-10-30). Home's and Market's 1D chips draw it, and their default range
 * follows this (`HOME_INDEX_DEFAULT_RANGE`).
 */
export const INDEX_INTRADAY_AVAILABLE = true as boolean

const MITRA_SAYS =
  'Sensitive follows the bigger, older companies. Float counts only shares that can trade.'

export const marketService = {
  indexSummary: async (
    range: IndexRange,
    indexKey = 'NEPSE',
    client?: QueryClient,
  ): Promise<IndexSummary> => {
    const api = marketEndpoints(client)
    const [indices, history, summary, status] = await Promise.all([
      api.indices(),
      api.indexHistory(indexKey, resolvedRange(range)),
      api.summary(),
      api.status(),
    ])
    const index = indices.rows.find((row) => row.key === indexKey)
    if (index === undefined) throw new Error(`${indexKey} is missing from the market feed`)
    const asOf = summary.as_of ?? status.as_of

    return indexSummarySchema.parse({
      index: indexDisplayName(index.name, index.key),
      indexKey: index.key,
      value: index.value,
      pointChange: index.change,
      percentChange: index.change_percent,
      asOf: { date: displayDate(asOf), time: displayTime(asOf) },
      isOpen: marketStateIsOpen(status.state),
      turnoverCr: rupeesToCrore(summary.turnover),
      volumeCrKitta: kittaToCrore(summary.shares_traded),
      breadth: breadthFromWire(summary.breadth),
      // One response, drawn two ways: each point carries its whole session,
      // so Line and Candles show the same sessions over the same window. Not
      // `/charts/history/`, whose `countback` window never matched this
      // response's calendar one and cost a second request every tick.
      // `candles` is absent — not empty — when the response carries no
      // sessions, so the panel falls back to the line and says why.
      points: chartPoints(history.points),
      candles: chartCandles(history.points),
    })
  },

  /**
   * The latest session as minute points, for the 1D chart. `null` while
   * `INDEX_INTRADAY_AVAILABLE` is off, with no request made.
   */
  indexIntraday: async (indexKey = 'NEPSE'): Promise<IndexIntraday | null> => {
    if (!INDEX_INTRADAY_AVAILABLE) return null
    const intraday = await marketApi.indexIntraday(indexKey)
    return indexIntradaySchema.parse({ points: intradayPoints(intraday.points) })
  },

  /**
   * The market mood: mm-be's Fear & Greed composite for the latest session, off the same
   * `/market/summary/` entry the NEPSE card and the rail already read, so no extra request.
   * `null` when mm-be has no reading for the latest session.
   */
  mood: async (client?: QueryClient): Promise<MarketMood | null> => {
    const summary = await marketEndpoints(client).summary()
    const mood = moodFromWire(summary.fear_greed)
    return mood === null ? null : marketMoodSchema.parse(mood)
  },

  /**
   * The mood's last `days` sessions, oldest first as served. A session that fails to parse is
   * left out, not the trend.
   */
  moodHistory: async (days: number): Promise<MoodDay[]> =>
    moodHistorySchema.parse((await marketApi.fearGreed(days)).map(moodDayFromWire)),

  /**
   * Daily OHLCV bars for one stock or index, oldest first.
   *
   * The series the stochastic, ADX, money flow index, VWAP and beta are
   * computed from: asked for by bar count, so a year of sessions is one request
   * whatever the calendar. `/market/stocks/{symbol}/price-history/` carries a
   * session per date as well, but takes a calendar range, and the Technical
   * tab needs 200 sessions that no range chip under a year can supply.
   *
   * A failure here is not swallowed: this is the whole subject of the screen
   * that reads it, so the caller's query goes to its error state and offers a
   * retry.
   */
  dailyBars: async (
    symbol: string,
    countback?: number,
    client?: QueryClient,
  ): Promise<DailyBars> => {
    const series = await marketEndpoints(client).chartHistory(symbol, countback)
    return dailyBarsSchema.parse({
      symbol: series.symbol,
      bars: barCandles(series) ?? [],
    })
  },

  tickerTape: async (client?: QueryClient): Promise<TickerTape> => {
    const api = marketEndpoints(client)
    const [indices, status] = await Promise.all([api.indices(), api.status()])
    return tickerTapeSchema.parse({
      status: marketStateIsOpen(status.state) ? 'open' : 'closed',
      items: indices.rows.map((row) => ({
        id: row.key.toLowerCase(),
        label: row.name,
        percentChange: row.change_percent,
        value: row.group === 'core' ? row.value : null,
      })),
    })
  },

  /**
   * The Market indices table: one row per `/api/v5/market/indices/` row, with the
   * 52-week high/low read straight off it (`fifty_two_week_high`/`_low`, nullable
   * on the wire and carried through as `null` rather than guessed).
   *
   * One request. The bounds were once joined in by key from
   * `/api/v5/market/fifty-two-week/` (D16), at the cost of two more calls and
   * with nothing to match: that list holds stocks only, so no index key was ever
   * on it. mm-be now publishes the pair on the index row itself.
   */
  indices: async (client?: QueryClient): Promise<IndicesPage> => {
    const indices = await marketEndpoints(client).indices()

    return indicesPageSchema.parse({
      rows: indices.rows.map((row) => ({
        id: row.key.toLowerCase(),
        name: indexDisplayName(row.name, row.key),
        group: row.group === 'core' ? 'core' : 'sector',
        close: row.value,
        percentChange: row.change_percent,
        high52: row.fifty_two_week_high,
        low52: row.fifty_two_week_low,
      })),
      mitraSays: MITRA_SAYS,
    })
  },
}
